import { type Modifiers } from '@dnd-kit/abstract';
import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { move } from '@dnd-kit/helpers';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import { GripVertical, XIcon } from 'lucide-react';
import { ReactNode, useRef } from 'react';

import { Button } from '@/components/core/button';

export type SortableListProps<T extends { id: string }> = {
  items: T[];
  onReorder: (items: T[]) => void;
  onDelete: (item: T) => void;
  children: (item: T) => ReactNode;
};

function Sortable<T extends { id: string }>({
  item,
  index,
  renderItem,
  onDelete,
  modifiers,
}: {
  item: T;
  index: number;
  renderItem: (item: T) => ReactNode;
  onDelete: (item: T) => void;
  modifiers: Modifiers;
}) {
  const { ref, handleRef } = useSortable({
    id: item.id,
    index,
    modifiers,
  });

  return (
    <li ref={ref} className="flex items-center gap-3 px-2 py-3">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Reorder item ${index + 1}`}
        className="cursor-grab text-muted-foreground hover:text-foreground"
        ref={handleRef}
      >
        <GripVertical className="size-5" />
      </Button>
      {renderItem(item)}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Remove item ${index + 1}`}
        className="ml-auto text-muted-foreground hover:text-foreground"
        onClick={() => onDelete(item)}
      >
        <XIcon className="size-4" />
      </Button>
    </li>
  );
}

export function SortableList<T extends { id: string }>({
  items,
  onReorder,
  onDelete,
  children,
}: SortableListProps<T>) {
  const containerRef = useRef<HTMLUListElement>(null);
  const modifiers: Modifiers = [
    RestrictToVerticalAxis,
    RestrictToElement.configure({
      element: () => containerRef.current,
    }),
  ];

  return (
    <DragDropProvider
      onDragOver={(event) => {
        onReorder(move(items, event));
      }}
    >
      <ul ref={containerRef} className="flex flex-col">
        {items.map((item, index) => (
          <Sortable
            key={item.id}
            item={item}
            index={index}
            renderItem={children}
            onDelete={onDelete}
            modifiers={modifiers}
          />
        ))}
      </ul>
    </DragDropProvider>
  );
}
